// Real-time task notifications.
//
// Connects to /ws/notifications, dispatches pushNotification on incoming
// task_notification frames so the bell badge updates instantly. Handles
// reconnect with exponential backoff and fires a sound + browser Notification
// for fresh frames (if the user has granted permission).
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { pushNotification, removeNotificationsForTask } from "../redux/slices/notifications";
import type { TaskNotification } from "../api/tasks";
import { useAuth } from "../contexts/AuthContext";
import { destinationFor } from "../utils/notificationRouting";
import { sessionGet } from "../utils/sessionStore";
import {
  SESSION_PLATFORM,
  getSessionDeviceId,
  shouldForceLogout,
  forceLogout,
  forceLogoutMessage,
  startSessionHeartbeat,
} from "../utils/session";

const PING_INTERVAL_MS = 25_000;
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const PREF_KEY = "notif:sound:enabled";

// Every stage, not just the terminal two. Between submitting a meeting and its
// summary arriving, the socket used to say nothing, which is why the workspace had
// to fall back to a timer. The stage frames are what let it stop.
const MEETLY_STAGES = [
  "RECORDING",
  "PROCESSING",
  "TRANSCRIBING",
  "TRANSCRIPT_READY",
  "SUMMARIZING",
  "COMPLETED",
  "FAILED",
] as const;

export type MeetlyStage = (typeof MEETLY_STAGES)[number];

export type MeetlyStatusChangedEvent = {
  meetingId: string;
  status: MeetlyStage;
  terminal: boolean;
  progress?: { transcribed?: number; total?: number; failed?: number } | null;
};

type SocketFrame = {
  type?: string;
  meeting_id?: unknown;
  status?: unknown;
  progress?: unknown;
  notification_id?: string;
  task_id?: string;
  kind?: string;
  title?: string;
  body?: string;
  created_at?: string;
  // Single-session force_logout frame fields.
  device_name?: unknown;
  reason?: unknown;
};

function isMeetlyStatusChangedFrame(data: SocketFrame): data is SocketFrame & {
  type: "meetly_status_changed";
  meeting_id: string;
  status: MeetlyStage;
} {
  return data.type === "meetly_status_changed"
    && typeof data.meeting_id === "string"
    && MEETLY_STAGES.includes(data.status as MeetlyStage);
}

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext;
}

// Single shared AudioContext — creating one per ping is wasteful and many
// browsers throttle/refuse repeated context creation.
let _audioCtx: AudioContext | null = null;
function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  try {
    const w = window as WindowWithWebkitAudio;
    const Ctx = w.AudioContext || w.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
  } catch {
    return null;
  }
  return _audioCtx;
}

// Browsers block audio until the user has interacted with the page. The first
// click/keypress/touch resumes the context so subsequent pings actually play.
let _audioUnlocked = false;
function unlockAudioOnGesture() {
  if (_audioUnlocked || typeof window === "undefined") return;
  const handler = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => undefined);
    }
    _audioUnlocked = true;
    window.removeEventListener("click", handler);
    window.removeEventListener("keydown", handler);
    window.removeEventListener("touchstart", handler);
  };
  window.addEventListener("click", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
  window.addEventListener("touchstart", handler, { once: true });
}

// Two-tone notification chime (~280ms): A5 → E6, soft attack, exponential decay.
// Clearly recognizable as a "ding" without being intrusive.
function playPing() {
  if (typeof window === "undefined") return;
  if (window.localStorage.getItem(PREF_KEY) === "0") return;
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => undefined);
  }
  try {
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.18; // overall volume — audible but not loud
    master.connect(ctx.destination);

    // Helper: schedule one tone with ADSR-like envelope.
    const tone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(1, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
      osc.connect(gain).connect(master);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.02);
    };

    tone(880, 0, 0.18);     // A5
    tone(1318.5, 0.12, 0.22); // E6 (a fifth up — pleasant interval)
  } catch {
    /* ignore audio failures */
  }
}

function maybeBrowserNotify(n: TaskNotification) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return; // only when tab hidden
  try {
    // Use the Teamlio brand icons (logo192 is the 192×192 PNG Chrome wants;
    // favicon-32 is the monochrome badge shown on Android lock-screen).
    const note = new Notification(n.title, {
      body: n.body || "",
      tag: `task-${n.task_id}`, // collapse multiple events for same task
      icon: "/logo192.png",
      badge: "/favicon-32.png",
    });
    note.onclick = () => {
      window.focus();
      // Route by namespace/kind so todo ("todo:") and leave ("leave:")
      // notifications don't land on a /tasks/<id> 404.
      window.location.href = destinationFor(n.kind, n.task_id, n.body);
    };
  } catch {
    /* silent */
  }
}

function buildWsUrl(token: string): string | null {
  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base) return null;
  // Convert https→wss, http→ws. Falls back to current location otherwise.
  const url = new URL(base, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // Strip any trailing path segments — base may end with /api/v1 etc.
  url.pathname = url.pathname.replace(/\/$/, "") + "/ws/notifications";
  url.searchParams.set("token", token);
  return url.toString();
}

export function useNotificationSocket() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.id || null;
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const stoppedRef = useRef(false);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Single-session enforcement: lives exactly as long as the authenticated
  // shell does (this hook is mounted from Layout.tsx), independent of the
  // per-user websocket effect below.
  useEffect(() => startSessionHeartbeat(), []);

  useEffect(() => {
    if (!userId) return;

    stoppedRef.current = false;

    // Arm the audio-unlock listener so the chime works on the first ping
    // after page load (browsers block audio until the user interacts).
    unlockAudioOnGesture();

    // One-time browser permission prompt — silent if dismissed.
    if (typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "default") {
      try { Notification.requestPermission().catch(() => undefined); } catch { /* ignore */ }
    }

    const connect = () => {
      if (stoppedRef.current) return;
      const token = sessionGet("accessToken");
      if (!token) return;
      const url = buildWsUrl(token);
      if (!url) return;

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try { ws.send("ping"); } catch { /* ignore */ }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        let data: SocketFrame | null = null;
        try { data = JSON.parse(event.data); } catch { return; }
        if (!data) return;
        // Single-session: another device took the teamlio_web slot.
        void (async () => {
          const deviceId = await getSessionDeviceId();
          if (shouldForceLogout(data, { platform: SESSION_PLATFORM, deviceId })) {
            // Unlike the heartbeat's 409 body, this frame carries a bare
            // device name — forceLogout() expects a complete sentence, and a
            // deactivation frame has no device to name at all, so
            // forceLogoutMessage builds the right one for each case.
            forceLogout(forceLogoutMessage(data));
          }
        })();
        if (isMeetlyStatusChangedFrame(data)) {
          const progress = (data as SocketFrame & { progress?: unknown }).progress;
          window.dispatchEvent(new CustomEvent<MeetlyStatusChangedEvent>("meetly-status-changed", {
            detail: {
              meetingId: data.meeting_id,
              status: data.status,
              terminal: data.status === "COMPLETED" || data.status === "FAILED",
              progress: progress && typeof progress === "object"
                ? progress as MeetlyStatusChangedEvent["progress"]
                : null,
            },
          }));
          return;
        }
        // A notification the server has retired because it is no longer true
        // (OVERDUE on a task whose due date moved out, or that was completed).
        if (data.type === "task_notifications_cleared") {
          if (!data.task_id || !data.kind) return;
          dispatch(removeNotificationsForTask({ taskId: data.task_id, kind: data.kind }));
          return;
        }
        if (data.type !== "task_notification") return;
        if (!data.notification_id || !data.task_id) return;
        const n: TaskNotification = {
          id: data.notification_id,
          task_id: data.task_id,
          user_id: userId,
          kind: data.kind ?? "",
          title: data.title ?? "",
          body: data.body ?? "",
          is_read: false,
          read_at: null,
          email_sent: false,
          email_sent_at: null,
          created_at: data.created_at ?? new Date().toISOString(),
        };
        dispatch(pushNotification(n));
        playPing();
        maybeBrowserNotify(n);
      };

      ws.onclose = () => {
        if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
        scheduleReconnect();
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      const attempt = Math.min(reconnectAttemptsRef.current + 1, 10);
      reconnectAttemptsRef.current = attempt;
      const delay = Math.min(MIN_BACKOFF_MS * 2 ** (attempt - 1), MAX_BACKOFF_MS);
      setTimeout(connect, delay);
    };

    connect();

    return () => {
      stoppedRef.current = true;
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(); } catch { /* ignore */ }
      }
    };
  }, [userId, dispatch]);
}

// Helpers exported so the bell UI can read/write the sound preference and
// preview the chime when the user toggles it on.
export function getSoundEnabled(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(PREF_KEY) !== "0";
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PREF_KEY, enabled ? "1" : "0");
}

// Preview the chime — used by the bell sound-toggle so the user immediately
// hears what an incoming notification will sound like.
export function previewNotificationSound(): void {
  // Bypass the preference check — this is an explicit user action.
  const prev = typeof window !== "undefined" ? window.localStorage.getItem(PREF_KEY) : null;
  if (prev === "0") window.localStorage.setItem(PREF_KEY, "1");
  playPing();
  if (prev === "0") window.localStorage.setItem(PREF_KEY, "0");
}
